import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Settings,
  Plus,
  Clock,
  FolderKanban,
  Timer,
  TrendingUp,
  ListTodo,
  AlertCircle,
} from "lucide-react";
import { CreateProjectDialog } from "@/components/project";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import logoPng from "@/assets/logo.png";

const TIMER_ENABLED = import.meta.env.VITE_ENABLE_TIMER === "true";

export function Sidebar() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const { profile, loading } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const menuItems = [
    { path: "/tasks", label: "Tasks", icon: ListTodo },
    { path: "/statistics", label: "Statistics", icon: TrendingUp },
    { path: "/today", label: "Today", icon: Clock },
    { path: "/backlogs", label: "Backlogs", icon: AlertCircle },
    ...(TIMER_ENABLED ? [{ path: "/time-tracking", label: "Time Tracking", icon: Timer }] : []),
    { path: "/projects", label: "Projects", icon: FolderKanban },
    { path: "/settings", label: "Settings", icon: Settings },
  ];

  const getInitials = (name: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const sidebarContent = (
    <>
{/* App Name */}
      <div className="p-4 border-b flex items-center justify-center">
        <img
          src={logoPng}
          alt="Todox"
          className="h-10 w-10"
        />
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 space-y-2">
        {menuItems.map((item) => {
          const Icon = item.icon;
          let isActive =
            location.pathname === item.path || location.pathname.startsWith(item.path + "/");
          // Special case: Projects tab should be active when viewing a project detail page
          if (
            item.path === "/projects" &&
            location.pathname.startsWith("/project/") &&
            !location.pathname.includes("/settings")
          ) {
            isActive = true;
          }
          // Special case: Settings tab should be active when viewing project settings
          if (item.path === "/settings" && location.pathname.includes("/settings")) {
            isActive = true;
          }
          return (
            <Link key={item.path} to={item.path}>
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className={`w-full justify-start ${isActive ? "bg-accent" : ""}`}
              >
                <Icon className="mr-2 h-4 w-4" />
                {item.label}
              </Button>
            </Link>
          );
        })}
      </nav>

      {/* New Project Button */}
      <div className="p-4 border-t">
        <Button onClick={() => setCreateDialogOpen(true)} className="w-full" size="lg">
          <Plus className="mr-2 h-4 w-4" />
          New Project
        </Button>
      </div>

      {/* Profile */}
      <div className="p-4 border-t">
        {loading ? (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-24 bg-muted rounded animate-pulse" />
              <div className="h-3 w-32 bg-muted rounded animate-pulse" />
            </div>
          </div>
        ) : profile ? (
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarImage
                src={profile.avatar_url || undefined}
                alt={profile.full_name || "User"}
              />
              <AvatarFallback>{getInitials(profile.full_name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{profile.full_name || "User"}</p>
              <p className="text-xs text-muted-foreground truncate">
                {profile.username || "No username"}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarFallback>U</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="text-sm font-medium">Not logged in</p>
            </div>
          </div>
        )}
      </div>

      <CreateProjectDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={() => {
          // Dialog will handle navigation or refresh
        }}
      />
    </>
  );

  // Mobile: Don't render sidebar, use BottomTabBar instead
  if (isMobile) {
    return null;
  }

  return (
    <aside className="hidden md:flex md:fixed left-0 top-0 h-screen w-64 border-r bg-background flex-col">
      {sidebarContent}
    </aside>
  );
}